import Event from "../../../../util/Event";
import EventDispatcher from "../../../../util/EventDispatcher";
import { isNull } from "../../../../util/TypeUtil";
import { DialogOption, DialogState, DialogTransition } from "../model/Dialog";
import DialogPlayerEvent from "./DialogPlayerEvent";

export default class DialogPlayer extends EventDispatcher {
    constructor(timeTickInterval) {
        super();
        
        this.timeTickInterval = timeTickInterval;
        this.executedCommands = [];
        this.position = new Position();
        this.debugMode = false;
        this.numCurrentOptions = 0;
        this.startingTime = -1;
        this.eventQueue = [];
        this.dialogTimeLeft = -1;
        this.loopTimer = setInterval(this.handleLoopTimer.bind(this), 20);
    }

    destroy() {
        this.clearLoopTimer();
        this.clearDialogTimers();

        this.currentDialog = null;
        this.eventQueue = null;
        this.executedCommands = null;
    }

    getMaxTime() {
        return this.currentDialog.getMaxTime();
    }

    getDialog() {
        return this.currentDialog;
    }

    getCurrentCommandSequence() {
        return this.position.currentCommandSequence;
    }

    setDialog(value, conditionSolver) {
        this.position.reset();
        this.currentDialog = value;
        this.conditionSolver = conditionSolver;
        this.clearDialogTimers();
        this.numCurrentOptions = 0;
        this.executedCommands = [];
    }

    next() {
        if (this.currentDialog === null)
            return;

        switch (this.position.level)
        {
            case Position.START:
                this.enterDialog();
                break;

            case Position.DIALOG:
                if (isNull(this.position.nextCommandSequence))
                {
                    this.exitDialog();
                }
                else
                {
                    this.enterCommandSequence(this.position.nextCommandSequence);
                }
                break;

            case Position.COMMAND_SEQUENCE:
                if (this.position.commandsIterator.hasNext())
                {
                    const currentCommand = this.position.commandsIterator.next();
                    if ((!currentCommand.isExecuteOnce() || !this.isExecuted(currentCommand)) && (currentCommand.getCondition() === null || currentCommand.getCondition().solve(this.conditionSolver)))
                    {
                        this.processCommand(currentCommand);
                    }
                    else
                    {
                        if (this.debugMode)
                        {
                            //this.dispatchEvent(new DialogPlayerEvent((currentCommand is IDialogOption) ? DialogPlayerEvent.SKIP_DIALOG_OPTION : DialogPlayerEvent.SKIP_DIALOG_COMMAND, currentCommand.data,(currentCommand is IDialogOption) ? currentCommand as IDialogOption : null));
                        }
                        this.next();
                    }
                }
                else
                {
                    this.eventQueue.push(new Event(DialogPlayerEvent.COMMAND_SEQUENCE_END));
                    if (this.position.currentCommandSequence instanceof DialogTransition)
                    {
                        this.exitCommandSequence(this.position.currentCommandSequence.getState());
                    }
                    else if (this.numCurrentOptions === 0)
                    {
                        this.exitCommandSequence();
                    }
                }
                break;

            case Position.END:
                break;
        }
    }

    takeOption(option) {
        this.setExecuted(option);

        if (! isNull(option) && ! isNull(option.getCommandSequence()))
        {
            if (this.position.level === Position.COMMAND_SEQUENCE)
            {
                this.clearEventQueue();
                this.exitCommandSequence(option.getCommandSequence());
            }
            else if (this.position.level === Position.DIALOG)
            {
                this.position.nextCommandSequence = option.getCommandSequence();
            }
        }
    }

    pauseDialogTime() {
    let dtl;
        if (! isNull(this.dialogTimer) && this.dialogTimeLeft === -1) {
            dtl = this.currentDialog.getMaxTime() - (new Date().getTime() - this.startingTime);
            this.clearDialogTimers();
            this.dialogTimeLeft = dtl;
        }
    }

    resumeDialogTime() {
        if (this.dialogTimeLeft !== -1) {
            this.startingTime = new Date().getTime() - (this.currentDialog.getMaxTime() - this.dialogTimeLeft);
            this.dialogTimer = setTimeout(this.handleDialogTimerComplete.bind(this), this.dialogTimeLeft);
            this.dialogTickTimer = setInterval(this.handleDialogTickTimer.bind(this), this.timeTickInterval);
            this.dialogTimeLeft = -1;
        }
    }

    reset() {
        this.setDialog(this.currentDialog, this.conditionSolver);
    }

    getDialogTimeRemaining() {
        if (this.startingTime !== -1) {
            var timeRemaining = new Date().getTime() - this.startingTime;
            if (timeRemaining < 0) {
                timeRemaining = 0;
            }
        }
        else {
            timeRemaining = -1;
        }

        return timeRemaining;
    }

    // ---------------- Protected functions

    enterDialog() {
        this.eventQueue.push(new Event(DialogPlayerEvent.START_DIALOG));
        if (this.currentDialog.getMaxTime() > 0) {
            this.startingTime = new Date().getTime();

            // Set up dialogTimer
            this.dialogTimer = setTimeout(this.handleDialogTimerComplete.bind(this), this.currentDialog.getMaxTime());

            // Set up dialogTickTimer
            this.dialogTickTimer = setInterval(this.handleDialogTickTimer.bind(this), this.timeTickInterval);
        }
        this.position.nextCommandSequence = this.currentDialog.getStartState();
        this.position.level = Position.DIALOG;
    }

    enterCommandSequence(commandSequence) {
        this.numCurrentOptions = 0;
        this.position.currentCommandSequence = commandSequence;
        this.position.nextCommandSequence = null;
        this.position.commandsIterator = commandSequence.getCommandsIterator();
        this.position.level = Position.COMMAND_SEQUENCE;
        if (commandSequence instanceof DialogState)
        {
            this.eventQueue.push(new Event(DialogPlayerEvent.ENTER_DIALOG_STATE));
        }
        else if (commandSequence instanceof DialogTransition)
        {
            this.eventQueue.push(new Event(DialogPlayerEvent.ENTER_DIALOG_TRANSITION));
        }
    }

    processCommand(command) {
        if (command instanceof DialogOption) {
            this.numCurrentOptions++;
            this.eventQueue.push(new Event(DialogPlayerEvent.DIALOG_OPTION, {"data": command.getData(), "option": command}));
        } else {
            this.setExecuted(command);
            this.eventQueue.push(new Event(DialogPlayerEvent.DIALOG_COMMAND, {"data": command.getData(), "option": command}));
        }
    }

    clearEventQueue() {
        this.eventQueue = [];
    }

    exitCommandSequence(nextCommandSequence) {
        var currentCommandSequence = this.position.currentCommandSequence;
        this.position.commandsIterator = null;
        this.position.currentCommandSequence = null;
        this.position.nextCommandSequence = nextCommandSequence;
        this.position.level = Position.DIALOG;
        this.numCurrentOptions = 0;
        if (currentCommandSequence instanceof DialogTransition) {
            this.eventQueue.push(new Event(DialogPlayerEvent.EXIT_DIALOG_TRANSITION));
        } else {
            this.eventQueue.push(new Event(DialogPlayerEvent.EXIT_DIALOG_STATE));
        }
    }

    exitDialog() {
        this.position.currentCommandSequence = null;
        this.position.nextCommandSequence = null;
        this.position.commandsIterator = null;
        this.position.level = Position.END;
        this.clearDialogTimers();
        this.eventQueue.push(new Event(DialogPlayerEvent.END_DIALOG));
    }

    clearLoopTimer() {
        if (! isNull(this.loopTimer))
        {
            clearInterval(this.loopTimer);
            this.loopTimer = null;
        }
    }

    clearDialogTimers() {
        if (! isNull(this.dialogTimer)) {
            clearTimeout(this.dialogTimer);
            this.dialogTimer = null;
        }
        if (! isNull(this.dialogTickTimer)) {
            clearInterval(this.dialogTickTimer);
            this.dialogTickTimer = null;
        }
        this.dialogTimeLeft = -1;
    }

    handleLoopTimer() {
        if (this.eventQueue.length > 0) {
            const dialogPlayerEvent = this.eventQueue.shift();
            this.dispatchEvent(dialogPlayerEvent.type, dialogPlayerEvent.data, this);
        }
    }

    handleDialogTimerComplete() {
        this.clearDialogTimers();
        this.eventQueue.push(new Event(DialogPlayerEvent.DIALOG_TIME_OUT));
    }

    handleDialogTickTimer() {
        const timePassed = new Date().getTime() - this.startingTime;
        this.dispatchEvent(DialogPlayerEvent.DIALOG_TIME_TICK, timePassed, this);
    }

    isExecuted(command) {
        return this.executedCommands.includes(command);
    }

    setExecuted(command) {
        this.executedCommands.push(command);
    }
}


// ---------------- Inner class Position ------------------------------

class Position {
    constructor() {
        this.level = Position.START;
        this.currentCommandSequence = null;
        this.commandsIterator = null;
        this.nextCommandSequence = null;
    }
    clone() {
        var clone = new Position();
        clone.level = this.level;
        clone.currentCommandSequence = this.currentCommandSequence;
        clone.commandsIterator = this.commandsIterator;
        clone.nextCommandSequence = this.nextCommandSequence;

        return clone;
    }
    destroy() {
        this.reset();
    }
    reset() {
        this.level = Position.START;
        this.currentCommandSequence = null;
        this.commandsIterator = null;
        this.nextCommandSequence = null;
    }
}

Position.START = 0;
Position.DIALOG = 1;
Position.COMMAND_SEQUENCE = 2;
Position.END = 3;


